"use client";

import posthog from "posthog-js";

/**
 * Demo-engagement conversion — the visitor's FIRST real AI prompt in the `/try`
 * demo, fired only AFTER the request clears the abuse gate (a rejected/bot
 * prompt never gets here). This is the core demo value — the AI catching
 * continuity issues — and a genuine action distinct from a page load, so it's
 * the event our "Demo-Images" Reddit campaign optimizes toward and measures
 * (its ad-group conversion goal is "Lead"). Pageviews are already covered by the
 * Reddit Pixel's `PageVisit` and PostHog's `$pageview`, so this must NOT fire on
 * load and must NOT fire per prompt.
 *
 * Sends to BOTH destinations so Reddit can optimize and PostHog can measure:
 *   - Reddit Ads pixel → standard "Lead" event
 *   - PostHog          → `demo_engaged` (utm_* on the event for per-ad breakdown)
 *
 * Deduped once per browser session via sessionStorage. Best-effort: never
 * throws, so a blocked pixel or an uninitialized PostHog can't break the demo,
 * and it no-ops during SSR.
 *
 * The server also reports an anonymous Reddit Conversions-API "Lead" keyed on
 * the `rdt_cid` click id (see `/api/demo/assistant/stream`) — that server path
 * survives ad-blockers (this client pixel can be blocked; a server call can't).
 * Note it does NOT beat query-stripping: `rdt_cid` is itself query-derived, so
 * if the Reddit mobile app strips the landing query, neither path can attribute.
 */
const LEAD_FIRED_KEY = "iloris_demo_lead_fired";

export function fireDemoEngagementOnce(): void {
    if (typeof window === "undefined") return;

    // Set the dedupe flag BEFORE firing so two rapid calls can't both slip
    // through, and a mid-flight failure can't cause a retry that double-counts.
    try {
        if (window.sessionStorage.getItem(LEAD_FIRED_KEY)) return;
        window.sessionStorage.setItem(LEAD_FIRED_KEY, "1");
    } catch {
        // sessionStorage can throw (private mode / disabled). Without a reliable
        // dedupe store we'd risk over-counting, so bail rather than fire blind.
        return;
    }

    const params = new URLSearchParams(window.location.search);
    const utm = {
        utm_content: params.get("utm_content") ?? undefined,
        utm_campaign: params.get("utm_campaign") ?? undefined,
        utm_term: params.get("utm_term") ?? undefined,
    };

    // Reddit Ads standard conversion event.
    try {
        window.rdt?.("track", "Lead");
    } catch {
        // Pixel not loaded / blocked — the server CAPI Lead still covers Reddit.
    }

    // PostHog demo-engagement event. PostHog also auto-attaches utm_* as person
    // properties, but we put them on the event too for easy funnel breakdown.
    try {
        posthog.capture("demo_engaged", utm);
    } catch {
        // PostHog not initialized (key unset) — safe to ignore.
    }
}
